import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, LogOutIcon, Car, Bike } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDuration, getExitChargeLabel, hasExitAmountDue } from '../../lib/parkingUtils';
import ReceiptImageInput from '../ReceiptImageInput';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ParkingExitDialog({ open, onOpenChange, plate, onSuccess }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [receiptImage, setReceiptImage] = useState(null);

  useEffect(() => {
    if (!open || !plate) {
      setPreview(null);
      setReceiptImage(null);
      return;
    }
    setLoading(true);
    axios
      .get(`${API}/vehicles/exit-preview`, { params: { plate }, withCredentials: true })
      .then(({ data }) => setPreview(data))
      .catch((err) => {
        toast.error(err.response?.data?.detail || 'No se pudo calcular el cobro');
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
  }, [open, plate, onOpenChange]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const { data } = await axios.post(
        `${API}/vehicles/exit`,
        { plate, receipt_image: receiptImage },
        { withCredentials: true },
      );
      const msg = data.is_member
        ? `Salida: ${data.plate} — SOCIO`
        : data.paid_at_entry
          ? `Salida: ${data.plate} — Ya pagado en entrada`
          : `Cobro: ${data.plate} — ${formatCurrency(data.amount_charged)}`;
      toast.success(msg);
      onSuccess?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al registrar salida');
    } finally {
      setConfirming(false);
    }
  };

  const chargeLabel = () => getExitChargeLabel(preview);
  const amountDue = hasExitAmountDue(preview);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md" data-testid="kiosk-exit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <LogOutIcon className="w-5 h-5 text-red-400" />
            Confirmar pago
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : preview ? (
          <div className="space-y-4 py-2">
            <div className="text-center">
              <p className="text-3xl font-mono font-bold tracking-widest">{preview.plate}</p>
              <div className="flex items-center justify-center gap-2 mt-2">
                {preview.vehicle_type === 'moto' ? (
                  <Badge variant="outline" className="text-amber-400 border-amber-400/50">
                    <Bike className="w-3 h-3 mr-1" />Moto
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-blue-400 border-blue-400/50">
                    <Car className="w-3 h-3 mr-1" />Auto
                  </Badge>
                )}
                {preview.is_member && <Badge className="bg-blue-600">SOCIO</Badge>}
              </div>
            </div>

            <div className="rounded-xl bg-slate-900/60 border border-slate-700 p-4 space-y-2">
              <div className="flex justify-between text-sm text-slate-300">
                <span>Tiempo adentro</span>
                <span className="font-mono">{formatDuration(preview.duration_minutes)}</span>
              </div>
              {preview.parking_amount > 0 && (
                <div className="flex justify-between text-sm text-slate-300">
                  <span>Estacionamiento</span>
                  <span className="font-mono">{formatCurrency(preview.parking_amount)}</span>
                </div>
              )}
              {preview.event_amount > 0 && (
                <div className="flex justify-between text-sm text-slate-300">
                  <span>Evento{preview.event_addon_name ? ` (${preview.event_addon_name})` : ''}</span>
                  <span className="font-mono text-amber-300">{formatCurrency(preview.event_amount)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-slate-700">
                <span className="text-slate-300 font-semibold">
                  {amountDue ? 'Total a cobrar' : 'Cobro adicional'}
                </span>
                <span
                  className={`text-2xl font-bold font-mono ${amountDue ? 'text-emerald-400' : 'text-amber-400'}`}
                  data-testid="exit-preview-amount"
                >
                  {chargeLabel()}
                </span>
              </div>
            </div>
            <div>
              <label className="mb-2 block text-sm text-slate-300">Comprobante de pago (opcional)</label>
              <ReceiptImageInput value={receiptImage} onChange={setReceiptImage} disabled={confirming} />
            </div>
          </div>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-slate-600 text-slate-300"
            disabled={confirming}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading || confirming || !preview}
            className="bg-red-600 hover:bg-red-700 font-semibold"
            data-testid="kiosk-confirm-exit-btn"
          >
            {confirming ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {amountDue ? 'Confirmar pago y salida' : 'Registrar salida'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
